import type { ReportSection } from '~/convex/lib/types';

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export function buildReportClipboardContent(sections: ReportSection[]) {
  const plainParts: string[] = [];
  const htmlParts: string[] = [];

  for (const section of sections) {
    const title = section.title.trim();
    const content = section.content.trim();
    plainParts.push(`${title}\n${content}`);
    htmlParts.push(
      `<p><strong>${escapeHtml(title)}</strong></p><p>${escapeHtml(content).replace(/\n/g, '<br>')}</p>`,
    );
  }

  return {
    plain: plainParts.join('\n\n'),
    html: htmlParts.join('\n\n'),
  };
}

export type CopyResult = { ok: true } | { ok: false; reason?: string };

/** Copy report to clipboard (web only). Rich text: section titles bold when pasting in Word etc. */
export async function copyReportToClipboard(
  sections: ReportSection[],
): Promise<CopyResult> {
  if (!sections?.length) {
    return { ok: false, reason: 'no_sections' };
  }

  const { plain, html } = buildReportClipboardContent(sections);
  const text = String(plain).trim();
  if (!text) {
    return { ok: false, reason: 'empty_content' };
  }

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plain], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      }),
    ]);
    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, reason: message };
  }
}
